import React, { useState, useEffect } from 'react';
import { TrackerEmail, EmailProcessRequest } from '../../../tracker/types/tracker.types';
import { emailsAPI } from '../../../tracker/api/emails.api';
import { AttachmentSelector } from '../../../emails/components/AttachmentSelector';
import './EmailToAffaireModal.styles.css';

interface EmailToAffaireModalProps {
  isOpen: boolean;
  email: TrackerEmail;
  onClose: () => void;
  onSuccess?: () => void;
  onSkip?: () => void;
}

export const EmailToAffaireModal: React.FC<EmailToAffaireModalProps> = ({
  isOpen,
  email,
  onClose,
  onSuccess,
  onSkip
}) => {
  // Add to dossier state - now supports multiple affaires
  const [affaireIds, setAffaireIds] = useState<string[]>([]);
  const [currentAffaireInput, setCurrentAffaireInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Attachment selection state
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<string[]>([]);

  // Helper to generate unique ID for attachments
  const getAttachmentUniqueId = (att: { id?: string; name: string }, idx: number) => {
    return att.id || `attachment-${idx}-${att.name}`;
  };

  // Initialize all attachments as selected
  useEffect(() => {
    if (email.attachments && email.attachments.length > 0) {
      const attachmentIds = email.attachments
        .filter(att => !att.isInline && !att.is_inline)
        .map((att, idx) => getAttachmentUniqueId(att, idx));
      setSelectedAttachmentIds(attachmentIds);
    }
  }, [email.attachments]);

  // Add affaire from manual input
  const handleAddAffaire = () => {
    const trimmed = currentAffaireInput.trim().replace(/\D/g, '');
    if (trimmed.length >= 4 && !affaireIds.includes(trimmed)) {
      setAffaireIds([...affaireIds, trimmed]);
      setCurrentAffaireInput('');
    }
  };

  // Remove affaire from selection
  const handleRemoveAffaire = (id: string) => {
    setAffaireIds(affaireIds.filter(a => a !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (affaireIds.length === 0) {
      alert('Veuillez entrer au moins un numéro d\'affaire');
      return;
    }

    setIsSubmitting(true);

    try {
      // Calculate excluded attachments
      let excludedIds: string[] = [];
      if (email.attachments && email.attachments.length > 0) {
        const allSelectableWithIds = email.attachments
          .filter(att => !att.isInline && !att.is_inline)
          .map((att, idx) => ({ uniqueId: getAttachmentUniqueId(att, idx) }));
        excludedIds = allSelectableWithIds
          .filter(att => !selectedAttachmentIds.includes(att.uniqueId))
          .map(att => att.uniqueId);
      }

      if (affaireIds.length === 1) {
        // Single affaire - use existing processEmail endpoint
        const requestData: EmailProcessRequest = {
          affaire_id: parseInt(affaireIds[0])
        };
        if (excludedIds.length > 0) {
          requestData.excluded_attachments = excludedIds;
        }
        const response = await emailsAPI.processEmail(email.id, requestData);
        if (!response.success) {
          throw new Error('Failed to process email');
        }
      } else {
        // Multiple affaires - use assignToMultipleAffaires endpoint
        const response = await emailsAPI.assignToMultipleAffaires(email.id, {
          affaire_ids: affaireIds.map(id => parseInt(id)),
          excluded_attachments: excludedIds.length > 0 ? excludedIds : undefined
        });
        if (!response.success) {
          throw new Error('Failed to assign email to multiple affaires');
        }
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error processing email:', error);
      alert('Erreur lors du traitement de l\'email');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    onSkip?.();
  };

  if (!isOpen) return null;

  return (
    <div className="email-affaire-modal-overlay" onClick={onClose}>
      <div className="email-affaire-modal" onClick={(e) => e.stopPropagation()}>
        <div className="email-affaire-modal-header">
          <h2>Lier l'email à un dossier ?</h2>
          <button className="email-affaire-modal-close" onClick={onClose}>&times;</button>
        </div>

        <p className="email-affaire-modal-description">
          Voulez-vous associer cet email à un ou plusieurs numéros d'affaire ?
        </p>

        <form onSubmit={handleSubmit} className="email-affaire-modal-form">
          {/* Selected Affaires Chips */}
          {affaireIds.length > 0 && (
            <div className="email-affaire-form-group">
              <label>Dossiers sélectionnés ({affaireIds.length})</label>
              <div className="email-affaire-selected-chips">
                {affaireIds.map(id => (
                  <span key={id} className="email-affaire-chip">
                    #{id}
                    <button
                      type="button"
                      className="email-affaire-chip-remove"
                      onClick={() => handleRemoveAffaire(id)}
                      title="Retirer"
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="email-affaire-form-group">
            <label htmlFor="affaire-id">
              {affaireIds.length > 0 ? 'Ajouter un autre dossier' : 'Numéro d\'affaire'}
            </label>
            <div className="email-affaire-input-row">
              <input
                id="affaire-id"
                type="text"
                value={currentAffaireInput}
                onChange={(e) => setCurrentAffaireInput(e.target.value.replace(/\D/g, ''))}
                placeholder="Ex: 12345"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddAffaire();
                  }
                }}
              />
              <button
                type="button"
                className="email-affaire-btn-add"
                onClick={handleAddAffaire}
                disabled={currentAffaireInput.length < 4 || affaireIds.includes(currentAffaireInput)}
              >
                + Ajouter
              </button>
            </div>
            {affaireIds.length === 0 && (
              <small className="email-affaire-form-hint">
                L'email sera lié à cette affaire et apparaîtra dans le Tracker
              </small>
            )}
            {affaireIds.length > 1 && (
              <small className="email-affaire-form-hint email-affaire-form-hint-info">
                L'email sera dupliqué dans {affaireIds.length} dossiers
              </small>
            )}
          </div>

          {/* Attachment Selection - show when at least one affaire is selected */}
          {email.has_attachments && email.attachments && email.attachments.length > 0 && affaireIds.length > 0 && (
            <AttachmentSelector
              attachments={email.attachments}
              emailId={email.id}
              selectedIds={selectedAttachmentIds}
              onSelectionChange={setSelectedAttachmentIds}
            />
          )}

          <div className="email-affaire-modal-actions">
            <button
              type="button"
              className="email-affaire-btn email-affaire-btn-secondary"
              onClick={handleSkip}
            >
              Passer
            </button>
            <button
              type="submit"
              className="email-affaire-btn email-affaire-btn-primary"
              disabled={isSubmitting || affaireIds.length === 0}
            >
              {isSubmitting ? 'Traitement...' : affaireIds.length > 1 ? `Lier à ${affaireIds.length} dossiers` : 'Lier au dossier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailToAffaireModal;
